import { type FormEvent, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '../components/layout/Layout'
import { useAuth } from '../hooks/useAuth'
import { registerAwsConnection, verifyAwsConnection } from '../lib/api'
import './RegistrationPage.css'

const registrationErrorMessages: Record<string, string> = {
  'missing-token': 'registration.missingTokenError',
  'product-mismatch': 'registration.productMismatchError',
  'invalid-token': 'registration.invalidTokenError'
}

type RegistrationFormState = {
  accountId: string
  roleArn: string
  installationId: string
  externalId: string
  stackId: string
}

function RegistrationPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, accessToken, loading: authLoading, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [submitting, setSubmitting] = useState(false)
  const [submissionError, setSubmissionError] = useState<string | null>(null)

  const registrationError = useMemo(() => {
    const errorCode = searchParams.get('error')
    if (!errorCode) {
      return null
    }

    const translationKey = registrationErrorMessages[errorCode]
    return translationKey ? t(translationKey) : errorCode
  }, [searchParams, t])

  const initialFormState = useMemo<RegistrationFormState>(
    () => ({
      accountId: searchParams.get('accountId') ?? '',
      roleArn: searchParams.get('roleArn') ?? '',
      installationId: searchParams.get('installationId') ?? '',
      externalId: searchParams.get('externalId') ?? '',
      stackId: searchParams.get('stackId') ?? ''
    }),
    [searchParams]
  )

  const [formState, setFormState] = useState<RegistrationFormState>(initialFormState)
  const error = submissionError ?? registrationError

  const isPrefilled = Boolean(
    initialFormState.accountId ||
      initialFormState.roleArn ||
      initialFormState.installationId ||
      initialFormState.externalId
  )

  const returnTo = `${location.pathname}${location.search}`

  const updateField = (field: keyof RegistrationFormState, value: string) => {
    setFormState(current => ({
      ...current,
      [field]: value
    }))
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!accessToken) {
      return
    }

    if (!formState.accountId || !formState.roleArn || !formState.installationId || !formState.externalId) {
      setSubmissionError(t('registration.missingFields'))
      return
    }

    setSubmitting(true)
    setSubmissionError(null)

    const registerResult = await registerAwsConnection(
      {
        accountId: formState.accountId,
        roleArn: formState.roleArn,
        installationId: formState.installationId,
        externalId: formState.externalId,
        ...(formState.stackId ? { stackId: formState.stackId } : {})
      },
      accessToken
    )

    if (!registerResult.ok) {
      setSubmitting(false)
      setSubmissionError(registerResult.error)
      return
    }

    const verifyResult = await verifyAwsConnection(registerResult.data.awsConnectionId, accessToken)
    setSubmitting(false)

    if (!verifyResult.ok) {
      setSubmissionError(verifyResult.error)
      return
    }

    void navigate(`/dashboard?connected=${encodeURIComponent(verifyResult.data.accountId)}`)
  }

  if (authLoading || submitting) {
    return (
      <Layout>
        <div className='registration'>
          <div className='registration-success'>
            <h1>{t('registration.completingLink')}</h1>
            <p>{t('registration.verifyingAccess')}</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className='registration'>
          <div className='registration-success'>
            <h1>{t('registration.title')}</h1>
            <p>{t('registration.signInPrompt')}</p>
            <button className='btn btn-primary' onClick={() => login(returnTo, 'sign-in')}>
              {t('registration.createAccountSignIn')}
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className='registration'>
        <div className='registration-success'>
          <h1>{t('registration.title')}</h1>
          <p>{isPrefilled ? t('registration.prefilledIntro') : t('registration.manualIntro')}</p>
          <p>
            {t('registration.dashboardLinkPrefix')} <Link to='/dashboard'>{t('registration.dashboardLink')}</Link>.
          </p>
        </div>

        {error ? (
          <div className='registration-error'>
            <h2>{t('registration.error')}</h2>
            <p>{error}</p>
          </div>
        ) : null}

        <form className='registration-form' onSubmit={handleSubmit}>
          <label>
            <span>{t('registration.accountIdLabel')}</span>
            <input
              value={formState.accountId}
              onChange={event => updateField('accountId', event.target.value)}
              placeholder='123456789012'
            />
          </label>

          <label>
            <span>{t('registration.roleArnLabel')}</span>
            <input
              value={formState.roleArn}
              onChange={event => updateField('roleArn', event.target.value)}
              placeholder='arn:aws:iam::123456789012:role/RunlessCustomerAccessRole'
            />
          </label>

          <label>
            <span>{t('registration.installationIdLabel')}</span>
            <input
              value={formState.installationId}
              onChange={event => updateField('installationId', event.target.value)}
              placeholder='inst_01JABCXYZ123'
            />
          </label>

          <label>
            <span>{t('registration.externalIdLabel')}</span>
            <input
              value={formState.externalId}
              onChange={event => updateField('externalId', event.target.value)}
              placeholder='ext_01JABCXYZ123'
            />
          </label>

          <label>
            <span>{t('registration.stackIdLabel')}</span>
            <input
              value={formState.stackId}
              onChange={event => updateField('stackId', event.target.value)}
              placeholder='arn:aws:cloudformation:eu-west-1:123456789012:stack/...'
            />
          </label>

          <button className='btn btn-primary registration-submit' type='submit'>
            {t('registration.completeLinkCta')}
          </button>
        </form>

        <div className='registration-steps'>
          <strong>{t('registration.nextSteps')}</strong>
          <ol>
            <li>{t('registration.step1')}</li>
            <li>{t('registration.step2')}</li>
            <li>{t('registration.step3')}</li>
          </ol>
        </div>
      </div>
    </Layout>
  )
}

export default RegistrationPage
